import { LockKeyhole } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { SignIn } from '@clerk/react';
import { useAuth, DEMO_ACCOUNTS, clerkEnabled } from '../../lib/auth';
import { isDemoMode } from '../../lib/repo';
import { Button, Card, Field, Input } from '../../components/ui';

interface FromState { from?: string }

export function LoginPage() {
  const { status, user, roles, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as FromState | null)?.from ?? '/admin';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHint, setShowHint] = useState(isDemoMode && !clerkEnabled);

  // Only ever redirect once auth has fully settled AND roles are known —
  // the guard must never act on partial state (audit bug B2).
  useEffect(() => {
    if (status === 'ready' && user && roles.length > 0) {
      navigate(from, { replace: true });
    }
  }, [status, user, roles, from, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    const res = await signIn(email, password);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    // Navigation happens in the effect above once roles resolve.
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md">
        <Card className="p-6">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-primary/10">
              <LockKeyhole className="h-5 w-5 text-primary" />
            </div>
            <h1 className="font-display text-2xl font-semibold">
              The <span className="italic text-primary">Story</span> Line Admin
            </h1>
            <p className="mt-1 text-sm text-muted">Sign in to manage the publication.</p>
          </div>

          {user && roles.length === 0 ? (
            <p className="mb-4 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning" role="alert">
              Signed in as {user.email}, but this account has no role yet. Ask an administrator to grant one.
            </p>
          ) : null}

          {clerkEnabled ? (
            // Clerk owns the form; it issues the session token our API verifies.
            <div className="flex justify-center">
              <SignIn />
            </div>
          ) : (
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              {error ? (
                <p className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger" role="alert">{error}</p>
              ) : null}

              <Field label="Email" error={undefined}>
                <Input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="editor@thestoryline.com" autoComplete="username" required
                />
              </Field>
              <Field label="Password">
                <Input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" autoComplete="current-password" required
                />
              </Field>

              <Button type="submit" className="w-full" disabled={busy || status === 'initialising'}>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          )}

          <p className="mt-4 text-center text-sm">
            <Link to="/" className="text-muted hover:text-primary">← Back to site</Link>
          </p>
        </Card>

        {showHint ? (
          <div className="mt-4 rounded-xl border border-border bg-card p-4 text-xs text-muted">
            <div className="flex items-start justify-between gap-3">
              <p className="font-medium text-foreground">Demo mode</p>
              <button type="button" className="text-muted hover:text-primary" onClick={() => setShowHint(false)} aria-label="Dismiss">✕</button>
            </div>
            <p className="mt-1">
              No authentication is configured, so sign in with one of these accounts. Setting
              <code className="mx-1 rounded bg-background px-1">VITE_CLERK_PUBLISHABLE_KEY</code>
              enables real sign-in, which the API verifies server-side.
            </p>
            <ul className="mt-2 space-y-1">
              {DEMO_ACCOUNTS.map((a) => (
                <li key={a.email}>
                  <code className="rounded bg-background px-1">{a.email}</code> · <code className="rounded bg-background px-1">{a.password}</code> · {a.role}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
